import React, { useState, useEffect } from "react";
import { DateTime } from "luxon";
import Icon from "@mdi/react";
import { mdiAlertCircle, mdiAlertCircleOutline, mdiCashRefund } from "@mdi/js";
import BaseModal from "./BaseModal";
import CustomFormInput from "./forms/CustomFormInput";
import DateInputWithPicker from "./DateInputWithPicker";
import { formatCurrencyFromMinor } from "../utils/currency";
import "./SickDayConfirmModal.css";

/**
 * SickDayConfirmModal Component
 *
 * Confirmation modal for recording a sick day. Handles two kinds of booking on the day:
 *
 * - UNPAID bookings — checkbox list, bulk-cancelled with the sick day (as before).
 * - PAID bookings — cannot be silently cancelled (money is involved), so each one
 *   requires an explicit instructor decision: Refund or Credit. Both cancel the lesson
 *   and return the money (via issueReturn full return). This mirrors the
 *   CancelClassesModal refund step. Confirm is blocked until every paid booking has a
 *   decision. (There is no "keep" — a sick day means the day is not being worked; a
 *   paid lesson must be refunded or credited. To keep a lesson, cancel out of the modal.)
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.onConfirm - (message, unpaidBookingIds, date, paidDecisions) => Promise
 *   where paidDecisions is [{ bookingId, action: 'refund'|'credit' }]
 * @param {Function} [props.onDateChange] - (newIsoDate) => void, parent re-derives the booking list
 * @param {Array} props.todaysBookings - active bookings for the date; each may be paid or unpaid
 * @param {string} [props.initialDate]
 * @param {boolean} [props.isProcessing=false]
 */
const SickDayConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    onDateChange,
    todaysBookings = [],
    initialDate,
    isProcessing = false,
}) => {
    const [cancellationMessage, setCancellationMessage] = useState(
        "I'm sorry, but I'm feeling unwell and need to cancel our lesson. I apologize for the inconvenience. Please reschedule using my next available timeslot."
    );
    const [selectedBookings, setSelectedBookings] = useState(new Set());
    // Per-paid-booking decision: bookingId -> 'refund' | 'credit' | 'keep' (undefined = undecided)
    const [paidDecisions, setPaidDecisions] = useState({});
    const [showEmptyWarning, setShowEmptyWarning] = useState(false);
    const [sickDate, setSickDate] = useState(initialDate || DateTime.local().toISODate());

    const unpaidBookings = todaysBookings.filter(b => !b.isPaid);
    const paidBookings = todaysBookings.filter(b => b.isPaid);

    // Reset the date when the modal OPENS only (see original note: must not depend on
    // todaysBookings or a date change inside the modal snaps the display back).
    useEffect(() => {
        if (isOpen) {
            setSickDate(initialDate || DateTime.local().toISODate());
        }
    }, [isOpen, initialDate]);

    // When the list changes (open or date picked): pre-check all UNPAID bookings, and
    // reset paid decisions to undecided (a new date's paid bookings are different rows).
    useEffect(() => {
        if (isOpen) {
            setSelectedBookings(new Set(todaysBookings.filter(b => !b.isPaid).map(b => b.id)));
            setPaidDecisions({});
        }
    }, [isOpen, todaysBookings]);

    // Every paid booking must have a decision before the sick day can be confirmed.
    const allPaidDecided = paidBookings.every(b => paidDecisions[b.id]);

    const handleConfirm = async () => {
        if (isProcessing || !allPaidDecided) return;

        const decisions = paidBookings.map(b => ({
            bookingId: b.id,
            action: paidDecisions[b.id],
        }));
        const unpaidSelected = Array.from(selectedBookings);

        // Only warn about "nothing happening" when there are no paid actions either.
        const anyPaidAction = decisions.some(d => d.action === "refund" || d.action === "credit");
        if (unpaidSelected.length === 0 && !anyPaidAction && todaysBookings.length > 0) {
            setShowEmptyWarning(true);
            return;
        }
        await onConfirm(cancellationMessage, unpaidSelected, sickDate, decisions);
    };

    const handleEmptyWarningConfirm = async () => {
        setShowEmptyWarning(false);
        if (isProcessing) return;
        const decisions = paidBookings.map(b => ({ bookingId: b.id, action: paidDecisions[b.id] }));
        await onConfirm(cancellationMessage, [], sickDate, decisions);
    };

    const handleToggleBooking = bookingId => {
        setSelectedBookings(prev => {
            const newSet = new Set(prev);
            if (newSet.has(bookingId)) newSet.delete(bookingId);
            else newSet.add(bookingId);
            return newSet;
        });
    };

    const setPaidDecision = (bookingId, action) => {
        setPaidDecisions(prev => ({ ...prev, [bookingId]: action }));
    };

    const handleSelectAll = () => setSelectedBookings(new Set(unpaidBookings.map(b => b.id)));
    const handleClearAll = () => setSelectedBookings(new Set());

    const handleClose = () => {
        if (!isProcessing) onClose();
    };

    const dateLabel = sickDate ? DateTime.fromISO(sickDate).toFormat("cccc, MMM d") : "the selected date";
    const confirmDisabled = isProcessing || !allPaidDecided;

    return (
        <>
            <BaseModal
                isOpen={isOpen}
                onClose={handleClose}
                title="Confirm Sick Day"
                icon={mdiAlertCircle}
                iconColor="var(--dl-color-sick-red)"
                maxWidth={560}
                footer={
                    <>
                        <button className="base-modal-cancel-btn" onClick={handleClose} disabled={isProcessing}>
                            Cancel
                        </button>
                        <button
                            className="base-modal-save-btn sick-day-confirm-btn"
                            onClick={handleConfirm}
                            disabled={confirmDisabled}
                            title={!allPaidDecided ? "Choose what to do with each paid booking first" : undefined}
                        >
                            {isProcessing ? "Recording..." : "Confirm Sick Day"}
                        </button>
                    </>
                }
            >
                <div className="sick-day-body">
                    {/* Date Selection */}
                    <div className="sick-day-date-section">
                        <label className="sick-day-date-label">Sick day date</label>
                        <DateInputWithPicker
                            id="sick-day-date"
                            value={sickDate}
                            onChange={newDate => {
                                setSickDate(newDate);
                                onDateChange?.(newDate);
                            }}
                            disablePast={true}
                        />
                    </div>

                    {/* Warning Message */}
                    <div className="sick-day-warning">
                        <Icon path={mdiAlertCircle} size={0.8} color="var(--dl-color-sick-red)" />
                        <p>
                            <strong>This will block your availability for {dateLabel}.</strong>
                            <br />
                            Students will be notified via message in the Mobile App.
                        </p>
                    </div>

                    {/* Unpaid bookings — bulk cancel */}
                    {unpaidBookings.length > 0 && (
                        <div className="sick-day-bookings">
                            <div className="sick-day-bookings-header">
                                <h3>Unpaid bookings to be cancelled ({unpaidBookings.length}):</h3>
                                <div className="sick-day-quick-actions">
                                    <button
                                        type="button"
                                        onClick={handleSelectAll}
                                        className="btn-quick-action"
                                        disabled={isProcessing || selectedBookings.size === unpaidBookings.length}
                                    >
                                        Select All
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleClearAll}
                                        className="btn-quick-action"
                                        disabled={isProcessing || selectedBookings.size === 0}
                                    >
                                        Clear All
                                    </button>
                                </div>
                            </div>

                            <ul className="bookings-list">
                                {unpaidBookings.map((booking, index) => (
                                    <li key={booking.id || index} className="booking-item">
                                        <div className="booking-checkbox-wrapper">
                                            <CustomFormInput
                                                type="checkbox"
                                                variant="turquoise"
                                                layout="standard"
                                                checked={selectedBookings.has(booking.id)}
                                                onChange={() => handleToggleBooking(booking.id)}
                                                disabled={isProcessing}
                                            />
                                            <div className="booking-details">
                                                <span className="booking-time">{booking.startTime}</span>
                                                <span className="booking-student">{booking.studentName}</span>
                                                {booking.status && (
                                                    <span className="booking-status-badge">{booking.status}</span>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>

                            <div className="selected-count">
                                {selectedBookings.size} of {unpaidBookings.length} booking(s) selected for cancellation
                            </div>
                        </div>
                    )}

                    {/* Paid bookings — require a per-booking decision */}
                    {paidBookings.length > 0 && (
                        <div className="sick-day-paid">
                            <div className="sick-day-paid-header">
                                <Icon path={mdiCashRefund} size={0.8} color="var(--dl-color-sick-red)" />
                                <h3>Paid bookings: choose what to do ({paidBookings.length})</h3>
                            </div>
                            <p className="form-hint">
                                These students have paid, so choose refund or credit. Both cancel the lesson and return
                                their money. Your message tells them to book another time with you.
                            </p>

                            <ul className="sick-day-paid-list">
                                {paidBookings.map(booking => (
                                    <li key={booking.id} className="sick-day-paid-item">
                                        <div className="sick-day-paid-info">
                                            <span className="booking-time">{booking.startTime}</span>
                                            <span className="booking-student">{booking.studentName}</span>
                                            {booking.refundableAmount > 0 && (
                                                <span className="sick-day-paid-amount">
                                                    {formatCurrencyFromMinor(
                                                        booking.refundableAmount,
                                                        booking.currency
                                                    )}{" "}
                                                    paid
                                                </span>
                                            )}
                                        </div>
                                        <div className="sick-day-paid-options">
                                            <CustomFormInput
                                                type="radio"
                                                variant="turquoise"
                                                layout="standard"
                                                name={`paid-${booking.id}`}
                                                checked={paidDecisions[booking.id] === "refund"}
                                                onChange={() => setPaidDecision(booking.id, "refund")}
                                                disabled={isProcessing}
                                                label="Refund"
                                            />
                                            {/* Credit is an account-holder privilege — not available to guests */}
                                            {!booking.isGuest && (
                                                <CustomFormInput
                                                    type="radio"
                                                    variant="turquoise"
                                                    layout="standard"
                                                    name={`paid-${booking.id}`}
                                                    checked={paidDecisions[booking.id] === "credit"}
                                                    onChange={() => setPaidDecision(booking.id, "credit")}
                                                    disabled={isProcessing}
                                                    label="Credit"
                                                />
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            {!allPaidDecided && (
                                <p className="sick-day-paid-warning">
                                    Choose an option for each paid booking to continue.
                                </p>
                            )}
                        </div>
                    )}

                    {/* No bookings at all */}
                    {todaysBookings.length === 0 && (
                        <div className="sick-day-info">
                            <p>You have no bookings scheduled for {dateLabel}.</p>
                            <p>Your availability will still be blocked for that day.</p>
                        </div>
                    )}

                    {/* Cancellation Message */}
                    {todaysBookings.length > 0 && (
                        <div className="sick-day-message-section">
                            <label htmlFor="cancellation-message" className="sick-day-message-label">
                                Message to students
                            </label>
                            <p className="form-hint">
                                Sent to every student whose booking is cancelled, refunded, or credited. Edit as
                                required.
                            </p>
                            <textarea
                                id="cancellation-message"
                                className="sick-day-message-textarea"
                                value={cancellationMessage}
                                onChange={e => setCancellationMessage(e.target.value)}
                                rows={4}
                                disabled={isProcessing}
                            />
                        </div>
                    )}
                </div>
            </BaseModal>

            {/* Empty Selection Warning — second portal modal, stacks correctly above main */}
            <BaseModal
                isOpen={showEmptyWarning}
                onClose={() => setShowEmptyWarning(false)}
                title="No Bookings Selected"
                icon={mdiAlertCircleOutline}
                iconColor="var(--dl-color-sick-red)"
                maxWidth={420}
                footer={
                    <>
                        <button className="base-modal-cancel-btn" onClick={() => setShowEmptyWarning(false)}>
                            Go Back
                        </button>
                        <button
                            className="base-modal-save-btn sick-day-confirm-btn"
                            onClick={handleEmptyWarningConfirm}
                            disabled={isProcessing}
                        >
                            Yes, Block Availability Only
                        </button>
                    </>
                }
            >
                <p>You haven't selected any bookings to cancel. Only your availability will be blocked for this day.</p>
                <p>Are you sure you want to continue?</p>
            </BaseModal>
        </>
    );
};

export default SickDayConfirmModal;
